import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getCredentialPanelStyles } from "../../../theme/panelStyles";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { openAIApi } from "../../../services/api/plenty/openai";
const OpenAICredentials = () => {
  const theme = useTheme();
  const cp = getCredentialPanelStyles(theme);

  const getErrorMessage = (error) => {
    if (typeof error === "string") return error;

    // Handle axios error structure
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail)) {
        // Handle validation errors array
        return detail
          .map((err) =>
            typeof err === "string"
              ? err
              : err.msg
                ? `${err.loc?.join(".")} ${err.msg}`
                : JSON.stringify(err),
          )
          .join("; ");
      }
      return JSON.stringify(detail);
    }

    if (error.response?.data?.message) {
      return typeof error.response.data.message === "string"
        ? error.response.data.message
        : JSON.stringify(error.response.data.message);
    }

    if (error.message) {
      return typeof error.message === "string"
        ? error.message
        : JSON.stringify(error.message);
    }

    return JSON.stringify(error);
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  //OpenAI
  const [openAICredentials, setOpenAICredentials] = useState(() => {
    try {
      const stored = localStorage.getItem("openAICredentials");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [openAICredentialDialog, setOpenAICredentialDialog] = useState(false);
  const [openAIEditingCredential, setOpenAIEditingCredential] = useState(null);
  const [openAIForm, setOpenAIForm] = useState({
    displayname: "",
    model: "",
    apiKey: "",
  });
  const [openAIModelSelection, setOpenAIModelSelection] =
    useState("gpt-4o-mini");
  const [openAITestLoading, setOpenAITestLoading] = useState(false);
  const [openAITestResult, setOpenAITestResult] = useState(null);

  useEffect(() => {
    // Load initial data with error handling
    const initializeData = async () => {
      try {
        // Load all data in parallel but handle each error separately
        const loadOperations = [
          loadOpenAICredentials().catch((error) => {
            console.error("Failed to load OpenAI credentials on init:", error);
            return null; // Continue with other operations
          }),
        ];

        await Promise.allSettled(loadOperations);
      } catch (error) {
        console.error("Unexpected error during initialization:", error);
        setError(getErrorMessage(error));
      }
    };

    initializeData();
  }, []);

  useEffect(() => {
    if (snackbarOpen) {
      const timer = setTimeout(() => setSnackbarOpen(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbarOpen]);

  //OpenAI
  const loadOpenAICredentials = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await openAIApi.getOpenAICredentials();
      const credentials = response.data || [];
      setOpenAICredentials(credentials);
      localStorage.setItem("openAICredentials", JSON.stringify(credentials));
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to load OpenAI credentials:", error);
      setError(getErrorMessage(error));
      setOpenAICredentials([]);
      localStorage.setItem("openAICredentials", JSON.stringify([]));
    } finally {
      setLoading(false);
    }
  };

  const handleEditOpenAICredential = (credential) => {
    setOpenAIEditingCredential(credential);
    const credentialModel = credential.model || "gpt-4o-mini";
    const isCustomModel = ![
      "gpt-5.1",
      "gpt-5-mini",
      "gpt-5-nano",
      "gpt-4.1",
      "gpt-4.1-mini",
      "gpt-4.1-nano",
      "o3",
      "o4-mini",
      "gpt-4o",
      "gpt-4o-mini",
      "gpt-4-turbo",
      "gpt-4",
      "gpt-3.5-turbo",
      "gpt-3.5-turbo-16k",
      "o1-preview",
      "o1-mini",
    ].includes(credentialModel);

    setOpenAIModelSelection(isCustomModel ? "custom" : credentialModel);
    setOpenAIForm({
      displayname: credential.display_name || "",
      model: credentialModel,
      apiKey: "", // Don't populate API key for security
    });
    setOpenAITestResult(null);
    setOpenAICredentialDialog(true);
  };

  const createOpenAICredential = async () => {
    if (!openAIForm.displayname || !openAIForm.apiKey) {
      setError("Please fill in display name and API key");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = {
        display_name: openAIForm.displayname,
        model: openAIForm.model || "gpt-4o-mini",
        token: openAIForm.apiKey,
      };

      const response = await openAIApi.createOpenAICredential(data);
      const newCredential = response.data || {};

      setOpenAICredentials((prev) => [...prev, newCredential]);
      setSuccess("OpenAI credential created successfully!");
      setSnackbarOpen(true);
      setOpenAICredentialDialog(false);
      setOpenAIEditingCredential(null);
      setOpenAIForm({
        displayname: "",
        model: "",
        apiKey: "",
      });
      setOpenAIModelSelection("gpt-4o-mini");
      setOpenAITestResult(null);
    } catch (error) {
      console.error("Failed to create OpenAI credential:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const updateOpenAICredential = async () => {
    if (!openAIForm.displayname) {
      setError("Please fill in display name");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = {
        display_name: openAIForm.displayname,
        model: openAIForm.model || "gpt-4o-mini",
      };

      // Only include API key if it was changed
      if (openAIForm.apiKey) {
        data.token = openAIForm.apiKey;
      }

      const response = await openAIApi.updateOpenAICredential(
        openAIEditingCredential.id,
        data,
      );
      const updatedCredential = response.data || {};

      setOpenAICredentials((prev) =>
        prev.map((cred) =>
          cred.id === updatedCredential.id ? updatedCredential : cred,
        ),
      );

      setSuccess("OpenAI credential updated successfully!");
      setSnackbarOpen(true);
      setOpenAIEditingCredential(null);
      setOpenAIForm({
        displayname: "",
        model: "",
        apiKey: "",
      });
      setOpenAIModelSelection("gpt-4o-mini");
      setOpenAITestResult(null);
      setOpenAICredentialDialog(false);
    } catch (error) {
      console.error("Failed to update OpenAI credential:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const deleteOpenAICredential = async (credentialId) => {
    if (
      !window.confirm("Are you sure you want to delete this OpenAI credential?")
    ) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await openAIApi.deleteOpenAICredential(credentialId);
      setOpenAICredentials((prev) =>
        prev.filter((cred) => cred.id !== credentialId),
      );
      setSuccess("OpenAI credential deleted successfully!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to delete OpenAI credential:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const toggleOpenAICredentialActive = async (credentialId) => {
    try {
      setLoading(true);
      setError(null);

      const response =
        await openAIApi.toggleActiveOpenAICredential(credentialId);
      const credentials = response.data || [];
      setOpenAICredentials(credentials);
      localStorage.setItem("openAICredentials", JSON.stringify(credentials));
      setSuccess("OpenAI credential status updated!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to toggle OpenAI credential:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const testOpenAICredential = async (apiKey) => {
    try {
      setOpenAITestLoading(true);
      setOpenAITestResult(null);
      setError(null);

      const response = await openAIApi.testOpenAICredential({
        token: apiKey || openAIForm.apiKey,
        model: openAIForm.model || openAIModelSelection || "gpt-4o-mini",
      });
      const result = response.data || {};

      if (result.valid || result.message) {
        setOpenAITestResult({
          success: true,
          message: result.message || "API key is valid and working!",
        });
        setSuccess("OpenAI credential is valid!");
        setSnackbarOpen(true);
        return true;
      } else {
        setOpenAITestResult({
          success: false,
          message: "OpenAI credential test failed",
        });
        setError("OpenAI credential test failed");
        return false;
      }
    } catch (error) {
      console.error("Failed to test OpenAI credential:", error);
      const errorMsg = getErrorMessage(error);
      setOpenAITestResult({
        success: false,
        message: errorMsg,
      });
      setError(errorMsg);
      return false;
    } finally {
      setOpenAITestLoading(false);
    }
  };

  const handleOpenAICredentialSubmit = () => {
    if (openAIEditingCredential) {
      updateOpenAICredential();
    } else {
      createOpenAICredential();
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card sx={cp.outerCard}>
          <CardContent sx={{ p: 4 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={4}
              sx={{
                ...cp.header,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  marginRight: "auto",
                  paddingRight: 2,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                  🤖 OpenAI API Credentials
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Manage your OpenAI API access credentials
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2, marginLeft: "auto" }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadOpenAICredentials}
                  disabled={loading}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  Reload
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setOpenAICredentialDialog(true);
                    setOpenAIEditingCredential(null);
                    setOpenAIForm({
                      displayname: "",
                      model: "",
                      apiKey: "",
                    });
                    setOpenAIModelSelection("gpt-4o-mini");
                    setOpenAITestResult(null);
                  }}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  Add Credentials
                </Button>
              </Box>
            </Box>

            <Alert
              severity="info"
              sx={cp.infoAlert}
              icon={<CheckCircleIcon sx={{ color: "info.main" }} />}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                📋 How to Get Your OpenAI API Key:
              </Typography>
              <Box component="ol" sx={{ m: 0, pl: 3 }}>
                <li>Go to OpenAI Platform</li>
                <li>Sign in or create an account</li>
                <li>Navigate to API Keys section</li>
                <li>Create a new API key</li>
                <li>Copy the key and paste it here</li>
                <li>Add billing information to enable API usage</li>
              </Box>
              <Typography variant="body2" sx={{ mt: 2, fontStyle: "italic" }}>
                For more information, visit{" "}
                <a
                  href="https://platform.openai.com/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#1976d2",
                    textDecoration: "underline",
                    fontWeight: "bold",
                  }}
                >
                  OpenAI API Keys
                </a>
              </Typography>
            </Alert>

            {openAICredentials.length === 0 ? (
              <Box sx={cp.emptyState}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: "text.secondary" }}
                >
                  No Credentials Found
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 3, color: "text.secondary" }}
                >
                  Add your first OpenAI credential to get started
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setOpenAICredentialDialog(true);
                    setOpenAIEditingCredential(null);
                    setOpenAIForm({
                      displayname: "",
                      model: "",
                      apiKey: "",
                    });
                    setOpenAIModelSelection("gpt-4o-mini");
                    setOpenAITestResult(null);
                  }}
                  sx={{
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    ...cp.ctaButton,
                  }}
                >
                  Add Your First Credential
                </Button>
              </Box>
            ) : (
              <List
                sx={{
                  width: "100%",
                  border: "2px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  boxShadow: 2,
                }}
              >
                {openAICredentials.map((credential, index) => (
                  <ListItem
                    key={credential.id}
                    divider={index < openAICredentials.length - 1}
                    sx={{
                      px: 4,
                      py: 3,
                      borderBottom:
                        index < openAICredentials.length - 1
                          ? "2px solid"
                          : "none",
                      borderColor: "divider",
                      "&:hover": {
                        backgroundColor: "action.hover",
                        transform: "translateX(4px)",
                      },
                      transition: "all 0.2s ease",
                      borderRadius:
                        index === 0
                          ? "8px 8px 0 0"
                          : index === openAICredentials.length - 1
                            ? "0 0 8px 8px"
                            : 0,
                    }}
                  >
                    <Grid container alignItems="center" spacing={3}>
                      <Grid item xs={12} md={8}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography variant="h6" sx={{ mr: 2 }}>
                            {credential.display_name || "Unnamed"}
                          </Typography>
                          <Chip
                            label={credential.active ? "Active" : "Inactive"}
                            size="small"
                            color={credential.active ? "success" : "default"}
                            sx={{
                              fontWeight: "bold",
                              borderRadius: 2,
                              boxShadow: 1,
                            }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            wordBreak: "break-all",
                            pr: 2,
                            color: "text.secondary",
                            fontFamily: "monospace",
                            backgroundColor: "grey.100",
                            p: 1,
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.300",
                          }}
                        >
                          Model: {credential.model || "gpt-4o-mini"}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            wordBreak: "break-all",
                            pr: 2,
                            color: "text.secondary",
                            fontFamily: "monospace",
                            backgroundColor: "grey.100",
                            p: 1,
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.300",
                            mt: 1,
                          }}
                        >
                          Created:{" "}
                          {new Date(credential.created_at).toLocaleDateString()}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={4}
                        sx={{
                          display: "flex",
                          justifyContent: { xs: "center", md: "flex-end" },
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        {/* Active Toggle Switch */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            mr: 2,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={credential.active}
                                onChange={() =>
                                  toggleOpenAICredentialActive(credential.id)
                                }
                                size="small"
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-checked": {
                                    color: "success.main",
                                  },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                    {
                                      backgroundColor: "success.main",
                                    },
                                }}
                              />
                            }
                            label=""
                            sx={{ m: 0 }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: credential.active
                                ? "success.main"
                                : "text.secondary",
                              fontWeight: "bold",
                              mt: 0.5,
                            }}
                          >
                            {credential.active ? "Active" : "Inactive"}
                          </Typography>
                        </Box>

                        <IconButton
                          title="Edit Credential"
                          onClick={() => handleEditOpenAICredential(credential)}
                          color="warning"
                          sx={{
                            backgroundColor: "warning.main",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "warning.dark",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => deleteOpenAICredential(credential.id)}
                          title="Delete Credential"
                          sx={{
                            backgroundColor: "error.main",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "error.dark",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* OpenAI Credential Dialog */}
      <Dialog
        open={openAICredentialDialog}
        onClose={() => {
          setOpenAICredentialDialog(false);
          setOpenAIEditingCredential(null);
          setOpenAIForm({
            displayname: "",
            model: "",
            apiKey: "",
          });
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
            ...cp.dialogPaper,
          },
        }}
      >
        <DialogTitle
          sx={{
            ...cp.dialogHeader,
            color: "white",
            textAlign: "center",
            py: 3,
            fontSize: "1.5rem",
            fontWeight: "bold",
            borderRadius: "12px 12px 0 0",
          }}
        >
          {openAIEditingCredential
            ? "✏️ Edit Credential"
            : "➕ Add New Credential"}
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              Enter your OpenAI API credentials below. All fields are required.
            </Typography>
          </Box>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                placeholder="My OpenAI API"
                value={openAIForm.displayname}
                onChange={(e) =>
                  setOpenAIForm((prev) => ({
                    ...prev,
                    displayname: e.target.value,
                  }))
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Model</InputLabel>
                <Select
                  value={
                    openAIModelSelection === "custom"
                      ? "custom"
                      : openAIForm.model || openAIModelSelection
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "custom") {
                      setOpenAIModelSelection("custom");
                      setOpenAIForm((prev) => ({ ...prev, model: "" }));
                    } else {
                      setOpenAIModelSelection(value);
                      setOpenAIForm((prev) => ({ ...prev, model: value }));
                    }
                  }}
                  label="Model"
                >
                  <MenuItem value="gpt-5.1">GPT-5.1</MenuItem>
                  <MenuItem value="gpt-5-mini">GPT-5 Mini</MenuItem>
                  <MenuItem value="gpt-5-nano">GPT-5 Nano</MenuItem>
                  <MenuItem value="gpt-4.1">GPT-4.1</MenuItem>
                  <MenuItem value="gpt-4.1-mini">GPT-4.1 Mini</MenuItem>
                  <MenuItem value="gpt-4.1-nano">GPT-4.1 Nano</MenuItem>
                  <MenuItem value="o3">O3</MenuItem>
                  <MenuItem value="o4-mini">O4 Mini</MenuItem>
                  <MenuItem value="gpt-4o">GPT-4o</MenuItem>
                  <MenuItem value="gpt-4o-mini">GPT-4o Mini</MenuItem>
                  <MenuItem value="gpt-4-turbo">GPT-4 Turbo</MenuItem>
                  <MenuItem value="gpt-4">GPT-4</MenuItem>
                  <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>
                  <MenuItem value="gpt-3.5-turbo-16k">
                    GPT-3.5 Turbo 16K
                  </MenuItem>
                  <MenuItem value="o1-preview">O1 Preview</MenuItem>
                  <MenuItem value="o1-mini">O1 Mini</MenuItem>
                  <MenuItem value="custom">Custom (Enter manually)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {openAIModelSelection === "custom" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Custom Model Name"
                  placeholder="Enter custom model name"
                  value={openAIForm.model}
                  onChange={(e) =>
                    setOpenAIForm((prev) => ({
                      ...prev,
                      model: e.target.value,
                    }))
                  }
                  helperText="Enter the exact model name (e.g., gpt-4-1106-preview)"
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Key"
                type="password"
                placeholder="sk-..."
                value={openAIForm.apiKey}
                onChange={(e) =>
                  setOpenAIForm((prev) => ({
                    ...prev,
                    apiKey: e.target.value,
                  }))
                }
                required={!openAIEditingCredential}
                helperText={
                  openAIEditingCredential
                    ? "Leave empty to keep existing key"
                    : "Enter your OpenAI API key starting with sk-"
                }
              />
            </Grid>

            {/* Test Button */}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                onClick={() => testOpenAICredential()}
                disabled={
                  !openAIForm.apiKey ||
                  openAITestLoading ||
                  (openAIModelSelection === "custom" && !openAIForm.model)
                }
                startIcon={
                  openAITestLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
              >
                {openAITestLoading ? "Testing..." : "Test API Key"}
              </Button>
            </Grid>

            {/* Test Result */}
            {openAITestResult && (
              <Grid item xs={12}>
                <Alert
                  severity={openAITestResult.success ? "success" : "error"}
                >
                  {openAITestResult.message}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  Get your API key from{" "}
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "inherit", fontWeight: "bold" }}
                  >
                    OpenAI Platform
                  </a>
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            pt: 0,
            justifyContent: "space-between",
            backgroundColor: "grey.50",
            borderRadius: "0 0 12px 12px",
          }}
        >
          <Button
            onClick={() => {
              setOpenAICredentialDialog(false);
              setOpenAIEditingCredential(null);
              setOpenAIForm({
                displayname: "",
                model: "",
                apiKey: "",
              });
              setOpenAIModelSelection("gpt-4o-mini");
              setOpenAITestResult(null);
            }}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              "&:hover": {
                backgroundColor: "grey.100",
              },
            }}
          >
            Cancel
          </Button>
          {!openAIEditingCredential && openAIForm.apiKey && (
            <Button
              onClick={() => testOpenAICredential(openAIForm.apiKey)}
              variant="outlined"
              disabled={loading || !openAIForm.apiKey}
              sx={{
                borderRadius: 2,
                px: 3,
                "&:hover": {
                  backgroundColor: "grey.100",
                },
              }}
            >
              {loading ? <CircularProgress size={20} /> : "Test Key"}
            </Button>
          )}
          <Button
            onClick={handleOpenAICredentialSubmit}
            variant="contained"
            disabled={
              loading ||
              !openAIForm.displayname ||
              (!openAIEditingCredential && !openAIForm.apiKey)
            }
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              ...cp.ctaButton,
              "&:hover": {
                background: "linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)",
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                background: "grey.400",
                color: "white",
              },
              transition: "all 0.3s ease",
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Processing...
              </Box>
            ) : openAIEditingCredential ? (
              "Update Credential"
            ) : (
              "Create Credential"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="Credentials reloaded successfully"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Grid>
  );
};

export default OpenAICredentials;
